// The fonts a document uses and how the Reader reads a font's name: for the Document fonts dialog (embedded, subset, not embedded, and what
// a viewer shows for a font that is not embedded), for matching the look of text being edited ("Match original"), and for replacing a
// font that is only named. TypeScript twin of rd_pdf::docfonts, rd_pdf::fontres (the name rules) and the style helpers of rd_pdf::paragraph.

import { PdfDocument } from "./document";
import { Dict, PName, PRef, Stream, type PObj } from "./parse";
import { incrementalUpdate } from "./incremental";

// ---- reading a font's name ------------------------------------------------------------------------------------------------------------

/** `ABCDEF+Arial` → `Arial`. */
export function stripSubsetTag(name: string): string {
    const m = /^([A-Z]{6})\+(.*)$/.exec(name);
    return m ? m[2]! : name.includes("+") ? name.split("+").pop()! : name;
}
export const hasSubsetTag = (name: string): boolean => /^[A-Z]{6}\+/.test(name);

/** The family a BaseFont names: no subset tag, no style words (`ABCDEF+Arial-BoldMT` → `Arial`). */
export function cleanFamily(baseFont: string): string {
    let n = stripSubsetTag(baseFont);
    n = n.split(",")[0]!.split("-")[0]!;
    for (const suffix of ["PSMT", "MT", "PS"]) if (n.endsWith(suffix) && n.length > suffix.length) { n = n.slice(0, -suffix.length); break; }
    return n;
}

export interface FontWish { bold: boolean; italic: boolean; serif: boolean; mono: boolean; symbol: boolean; family: string | null }

/** A request distilled from a PDF font's name and the descriptor /Flags. */
export function wishFromName(baseFont: string, flags = 0): FontWish {
    const name = baseFont.split("+").pop() ?? baseFont;
    const lower = name.toLowerCase().replace(/[ ,_]/g, "-");
    const has = (s: string) => lower.includes(s);
    const sans = has("sans") || has("arial") || has("helvetica");
    const families: Array<[string[], string]> = [
        [["arial", "helvetica", "liberationsans", "arimo"], "Arial"], [["timesnewroman", "times", "liberationserif", "tinos"], "Times New Roman"], [["couriernew", "courier", "liberationmono", "cousine"], "Courier New"],
        [["calibri"], "Calibri"], [["cambria"], "Cambria"], [["verdana"], "Verdana"], [["tahoma"], "Tahoma"], [["georgia"], "Georgia"], [["trebuchet"], "Trebuchet MS"], [["consolas"], "Consolas"], [["segoe"], "Segoe UI"]
    ];
    const hit = families.find(([keys]) => keys.some(has));
    return {
        bold: has("bold") || has("black") || has("heavy") || has("semibold") || has("demi") || (flags & (1 << 18)) !== 0,
        italic: has("italic") || has("oblique") || (flags & (1 << 6)) !== 0,
        mono: has("courier") || has("mono") || has("consolas") || has("lucidaconsole") || (flags & 1) !== 0,
        serif: has("times") || (has("serif") && !has("sans")) || has("georgia") || has("garamond") || has("palatino") || has("bookman") || has("century") || has("cambria") || ((flags & 2) !== 0 && !sans),
        symbol: has("symbol") || has("zapf") || has("dingbat") || has("wingdings"),
        family: hit ? hit[1] : cleanFamily(baseFont) || null
    };
}

/** The standard 14 fonts (and their common spellings) are never embedded by a file. */
export function isStandard14(baseFont: string): boolean {
    const n = stripSubsetTag(baseFont).toLowerCase().replace(/[ _]/g, "-");
    const stem = n.split(/[,-]/)[0]!;
    return ["helvetica", "courier", "symbol", "zapfdingbats"].includes(stem) || n.startsWith("times-") || n === "times" || n === "times-roman";
}

export type BundledFamily = "Liberation Sans" | "Liberation Serif" | "Liberation Mono" | "Noto Sans";

/** The bundled family whose metrics match a name (Arial and Helvetica, Times New Roman and Times, Courier New and Courier). */
export function metricTwin(baseFont: string): BundledFamily | null {
    const n = stripSubsetTag(baseFont).toLowerCase().replace(/[ _]/g, "");
    if (n.startsWith("helvetica") || (n.startsWith("arial") && !n.startsWith("arialblack") && !n.startsWith("arialnarrow")) || n.startsWith("liberationsans") || n.startsWith("arimo")) return "Liberation Sans";
    if (n.startsWith("times") || n.startsWith("liberationserif") || n.startsWith("tinos")) return "Liberation Serif";
    if (n.startsWith("courier") || n.startsWith("liberationmono") || n.startsWith("cousine")) return "Liberation Mono";
    return null;
}

export const bundledByClass = (w: Pick<FontWish, "mono" | "serif">): BundledFamily => (w.mono ? "Liberation Mono" : w.serif ? "Liberation Serif" : "Liberation Sans");

// ---- the document's fonts -----------------------------------------------------------------------------------------------------------------

export type Embedding = "full" | "subset" | "none";
export type ProgramKind = "none" | "sfnt" | "cff" | "type1" | "type3";

export interface DocFont {
    /** The font dictionary's object number, when it is an indirect object. */
    object: number | null;
    name: string;
    baseFont: string;
    subtype: string;
    embedding: Embedding;
    program: ProgramKind;
    bold: boolean;
    italic: boolean;
    flags: number;
    /** 0-based pages that use it. */
    pages: number[];
    /** What a viewer shows when the font is not embedded: the bundled family a substitute would use. Null for an embedded font. */
    shownAs: { family: string; metricTwin: boolean } | null;
}

const descriptorOf = (fd: Dict): Dict | null => {
    const d = fd.dict("FontDescriptor");
    if (d) return d;
    const df = fd.arr("DescendantFonts");
    const first = df && fd.xref ? fd.xref.resolve(df[0] as PObj) : null;
    return first instanceof Dict ? first.dict("FontDescriptor") : null;
};

/** The embedded program of a font: its kind and, for TrueType / OpenType, the bytes. */
export function fontProgram(fd: Dict): { kind: ProgramKind; bytes: Uint8Array | null } {
    if (fd.name("Subtype") === "Type3") return { kind: "type3", bytes: null };
    const desc = descriptorOf(fd);
    if (!desc) return { kind: "none", bytes: null };
    const f2 = desc.get("FontFile2");
    if (f2 instanceof Stream) return { kind: "sfnt", bytes: f2.bytes() };
    const f3 = desc.get("FontFile3");
    if (f3 instanceof Stream) {
        const b = f3.bytes();
        const tag = String.fromCharCode(b[0] ?? 0, b[1] ?? 0, b[2] ?? 0, b[3] ?? 0);
        return tag === "OTTO" || tag === "true" || (b[0] === 0 && b[1] === 1 && b[2] === 0 && b[3] === 0) ? { kind: "sfnt", bytes: b } : { kind: "cff", bytes: null };
    }
    if (desc.get("FontFile") instanceof Stream) return { kind: "type1", bytes: null };
    return { kind: "none", bytes: null };
}

/** Bold and italic as the font declares them: name words, descriptor flags, weight and italic angle. */
export function traitsOf(fd: Dict): { bold: boolean; italic: boolean; flags: number } {
    const base = fd.name("BaseFont") ?? "";
    const desc = descriptorOf(fd);
    const flags = desc ? desc.num("Flags") : 0;
    const w = wishFromName(base, flags);
    return { bold: w.bold || (desc ? desc.num("FontWeight") >= 700 : false), italic: w.italic || (desc ? Math.abs(desc.num("ItalicAngle")) > 0.5 : false), flags };
}

function fontEntries(res: Dict | null): Array<{ key: string; ref: PRef | null; dict: Dict }> {
    const fonts = res?.dict("Font");
    if (!fonts) return [];
    const out: Array<{ key: string; ref: PRef | null; dict: Dict }> = [];
    for (const k of fonts.keys()) {
        const raw = fonts.raw(k);
        const d = fonts.get(k);
        if (d instanceof Dict) out.push({ key: k, ref: raw instanceof PRef ? raw : null, dict: d });
    }
    return out;
}

/** Every font the document's pages use (also those of the forms they draw), with how each is stored. */
export function documentFonts(doc: PdfDocument): DocFont[] {
    const out: DocFont[] = [];
    const seenRes = new Set<Dict>();
    const collect = (res: Dict | null, page: number, depth: number) => {
        if (!res || seenRes.has(res) && depth > 0) return;
        seenRes.add(res);
        for (const { ref, dict } of fontEntries(res)) {
            if (ref) { const have = out.find((f) => f.object === ref.num); if (have) { if (!have.pages.includes(page)) have.pages.push(page); continue; } }
            const baseFont = dict.name("BaseFont") ?? "";
            const prog = fontProgram(dict);
            const t = traitsOf(dict);
            const embedding: Embedding = prog.kind === "none" ? "none" : hasSubsetTag(baseFont) ? "subset" : "full";
            const wish = wishFromName(baseFont, t.flags);
            const twin = metricTwin(baseFont);
            out.push({
                object: ref ? ref.num : null, name: stripSubsetTag(baseFont), baseFont, subtype: dict.name("Subtype") ?? "", embedding, program: prog.kind, bold: t.bold, italic: t.italic, flags: t.flags, pages: [page],
                shownAs: embedding === "none" ? { family: twin ?? bundledByClass(wish), metricTwin: twin !== null || isStandard14(baseFont) } : null
            });
        }
        if (depth >= 3) return;
        const xo = res.dict("XObject");
        if (xo) for (const k of xo.keys()) { const s = xo.get(k); if (s instanceof Stream && s.dict.name("Subtype") === "Form") collect(s.dict.dict("Resources"), page, depth + 1); }
    };
    const pages = doc.pages();
    pages.forEach((p, i) => collect(p.resources, i, 0));
    const merged: DocFont[] = [];
    for (const f of out) {
        const m = merged.find((x) => x.name === f.name && x.embedding === f.embedding && x.subtype === f.subtype);
        if (m) { for (const p of f.pages) if (!m.pages.includes(p)) m.pages.push(p); } else merged.push(f);
    }
    for (const m of merged) m.pages.sort((a, b) => a - b);
    return merged.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

/** `Embedded`, `Embedded subset`, `Not embedded - substituted` (a bundled font shows in its place; a PC may have the real one). */
export function embeddingWord(f: DocFont): string {
    return f.embedding === "full" ? "Embedded" : f.embedding === "subset" ? "Embedded subset" : "Not embedded";
}

/** The font a page's text calls `baseFont`, from the page's resources (null when the page does not declare it). */
export function runFontOf(doc: PdfDocument, pageIndex: number, baseFont: string): { baseFont: string; bold: boolean; italic: boolean; flags: number; program: { kind: ProgramKind; bytes: Uint8Array | null }; subset: boolean } | null {
    const want = stripSubsetTag(baseFont);
    const page = doc.pages()[pageIndex];
    if (!page) return null;
    for (const { dict } of fontEntries(page.resources)) {
        const name = dict.name("BaseFont") ?? "";
        if (stripSubsetTag(name) !== want) continue;
        const t = traitsOf(dict);
        return { baseFont: name, bold: t.bold, italic: t.italic, flags: t.flags, program: fontProgram(dict), subset: hasSubsetTag(name) };
    }
    return null;
}

/**
 * Replaces the font every not-embedded simple font called `oldName` (the name without a subset tag) names: Calibri → Arial, and every
 * viewer then shows the new one. An embedded font keeps its glyphs and cannot be swapped this way. Returns the new bytes and how many font
 * dictionaries changed; throws with the reason when none can change.
 */
export async function replaceFontName(original: Uint8Array, oldName: string, newBaseFont: string): Promise<{ bytes: Uint8Array; changed: number }> {
    const nb = newBaseFont.trim().replace(/ /g, "");
    if (!nb) throw new Error("Give the name of the font to use");
    let changed = 0;
    let foundEmbedded = false;
    const bytes = await incrementalUpdate(original, (c) => {
        const doc = c.doc;
        const done = new Set<number>();
        const nameObj = new PName(nb);
        const pages = doc.pages();
        const retarget = (fd: Dict): Dict => {
            const d = new Dict(doc.xref);
            for (const k of fd.keys()) d.set(k, fd.raw(k) as PObj);
            d.set("BaseFont", nameObj);
            const desc = fd.dict("FontDescriptor");
            if (desc && !(fd.raw("FontDescriptor") instanceof PRef)) { const dd = new Dict(doc.xref); for (const k of desc.keys()) dd.set(k, desc.raw(k) as PObj); dd.set("FontName", nameObj); d.set("FontDescriptor", dd); }
            return d;
        };
        pages.forEach((p, pi) => {
            const pref = c.pageRef(pi);
            const fonts = p.resources.dict("Font");
            if (!fonts || !pref) return;
            let pageChanged = false;
            const newFonts = new Dict(doc.xref);
            for (const k of fonts.keys()) newFonts.set(k, fonts.raw(k) as PObj);
            for (const k of fonts.keys()) {
                const fd = fonts.get(k);
                if (!(fd instanceof Dict) || stripSubsetTag(fd.name("BaseFont") ?? "") !== oldName) continue;
                if (fontProgram(fd).kind !== "none") { foundEmbedded = true; continue; }
                const st = fd.name("Subtype");
                if (st === "Type0" || st === "Type3") continue;
                const raw = fonts.raw(k);
                if (raw instanceof PRef) {
                    if (done.has(raw.num)) continue;
                    done.add(raw.num);
                    const nd = retarget(fd);
                    const desc = fd.raw("FontDescriptor");
                    if (desc instanceof PRef && !done.has(-desc.num)) { done.add(-desc.num); const dd = c.copy(desc.num); dd.set("FontName", nameObj); c.put(desc.num, dd); }
                    c.put(raw.num, nd);
                    changed++;
                } else { newFonts.set(k, retarget(fd)); pageChanged = true; changed++; }
            }
            if (pageChanged) {
                const res = new Dict(doc.xref);
                for (const k of p.resources.keys()) res.set(k, p.resources.raw(k) as PObj);
                res.set("Font", newFonts);
                const pd = c.copy(pref.num);
                pd.set("Resources", res);
                c.put(pref.num, pd);
            }
        });
    });
    if (!changed) throw new Error(foundEmbedded ? "This font is embedded: its letters are in the file. Edit the text to set it in another font." : "That font is not in the document");
    return { bytes, changed };
}

// ---- the look of a run, in words, and the colour of its edit surface ----------------------------------------------------------------------

const lin = (c: number) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
export const relLum = (c: [number, number, number]): number => 0.2126 * lin(c[0]) + 0.7152 * lin(c[1]) + 0.0722 * lin(c[2]);
/** How well `a` reads on `b` (the WCAG contrast ratio: 1 for the same colour, 21 for black on white). */
export function contrastRatio(a: [number, number, number], b: [number, number, number]): number {
    const la = relLum(a), lb = relLum(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** The colour a run's edit box is painted in: the page's own background under the text when the text can be read on it, otherwise a neutral that contrasts with the text (white text on an unknown background gets a dark surface). */
export function editSurface(text: [number, number, number], sampled: [number, number, number] | null): [number, number, number] {
    if (sampled && contrastRatio(text, sampled) >= 2) return sampled;
    return relLum(text) > 0.3 ? [0x24, 0x28, 0x30] : [0xff, 0xff, 0xff];
}

/** The colour most of the pixels in `region` (x0, y0, x1, y1 in device pixels) have, ignoring those close to `avoid` (the text colour and its anti-aliasing). */
export function dominantColor(rgba: Uint8ClampedArray | Uint8Array, w: number, h: number, region: [number, number, number, number], avoid: [number, number, number]): [number, number, number] | null {
    const cl = (v: number, m: number) => Math.min(m, Math.max(0, Math.round(v)));
    const [x0, y0, x1, y1] = [cl(region[0], w), cl(region[1], h), cl(region[2], w), cl(region[3], h)];
    const buckets = new Map<number, { n: number; sum: [number, number, number] }>();
    for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) {
        const i = (y * w + x) * 4;
        const r = rgba[i]!, g = rgba[i + 1]!, b = rgba[i + 2]!;
        if (Math.abs(r - avoid[0]) <= 40 && Math.abs(g - avoid[1]) <= 40 && Math.abs(b - avoid[2]) <= 40) continue;
        const key = ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);
        const e = buckets.get(key) ?? { n: 0, sum: [0, 0, 0] as [number, number, number] };
        e.n++; e.sum[0] += r; e.sum[1] += g; e.sum[2] += b;
        buckets.set(key, e);
    }
    let best: { n: number; sum: [number, number, number]; key: number } | null = null;
    for (const [key, e] of buckets) if (!best || e.n > best.n || (e.n === best.n && key > best.key)) best = { ...e, key };
    return best ? [Math.floor(best.sum[0] / best.n), Math.floor(best.sum[1] / best.n), Math.floor(best.sum[2] / best.n)] : null;
}

export function colorName(c: [number, number, number]): string {
    if (c.every((v) => v >= 0xf4)) return "white";
    if (c.every((v) => v <= 0x0b)) return "black";
    return "#" + c.map((v) => v.toString(16).padStart(2, "0").toUpperCase()).join("");
}

/** The look of a run in words, e.g. "Arial Bold 24 pt, white". */
export function describeStyle(family: string, bold: boolean, italic: boolean, size: number, color: [number, number, number]): string {
    return `${family}${bold ? " Bold" : ""}${italic ? " Italic" : ""} ${Math.round(size * 10) / 10} pt, ${colorName(color)}`;
}

/** "#rrggbb" or "rgb(r,g,b)" as components. */
export function parseCssColor(s: string | undefined): [number, number, number] | null {
    if (!s) return null;
    const hx = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(s);
    if (hx) return [parseInt(hx[1]!, 16), parseInt(hx[2]!, 16), parseInt(hx[3]!, 16)];
    const rg = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(s);
    return rg ? [Number(rg[1]), Number(rg[2]), Number(rg[3])] : null;
}
